/**
 * shiftWindow — khoảng trạng thái đơn mà một người trong ê-kíp còn làm việc
 * trên ca của mình: nộp ảnh nghiệm thu, bấm hoàn tất ca.
 *
 * `editing` nằm trong khoảng CÓ CHỦ Ý. Từ Đ6 (spec 2026-08-23 bảng T1), thợ
 * chụp dán link giữa buổi là đơn sang sửa hình NGAY, không chờ cả ê-kíp bấm
 * xong ca. Người trang điểm thường bấm sau đó — server đã nới đúng hai trạng
 * thái này (`ekipschedule.CompleteShift`) và vẫn giữ rào đủ cặp ảnh
 * trước/sau. Màn hình chỉ nhận `shooting` là cắt mất đường nộp ảnh của họ:
 * nút biến mất, cờ vàng "thiếu ảnh nghiệm thu makeup" treo vĩnh viễn.
 *
 * Sửa khoảng này phải sửa CẢ hai bên — đây là bản của màn hình, bản thật nằm
 * ở server.
 */
export const OPEN_SHIFT_STATUSES = ["shooting", "editing"] as const;

/** Ca còn mở cho người trong ê-kíp thao tác (nộp ảnh, hoàn tất). */
export function isShiftOpen(status: string | null | undefined): boolean {
  return status === "shooting" || status === "editing";
}

/**
 * isMakeupProofOpen — cửa MUỘN của riêng người trang điểm (quyết định 0192).
 *
 * `dueAt` là hạn chót máy chủ tính (`EkipShift.makeupProofDueAt`): 24 giờ sau
 * giờ kết thúc ca, hoặc mốc trưởng cửa hàng đã gia hạn. CỐ Ý không tự tính lại
 * mốc ấy ở đây — luật nằm ở `order.MakeupProofDeadline` phía máy chủ, chép sang
 * đây là đẻ ra bản thứ hai rồi hai bên trôi khỏi nhau.
 *
 * Rỗng = đơn chưa có ngày chụp, cửa không đóng.
 */
export function isMakeupProofOpen(
  dueAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!dueAt) return true;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return true;
  return now.getTime() <= due;
}

/**
 * canMakeupWorkShift — người trang điểm còn nộp ảnh / bấm hoàn tất được không.
 *
 * Ca đang chạy thì mở như mọi khi; ca đã đóng thì còn mở đúng trong hạn nộp
 * ảnh. Thợ chụp KHÔNG dùng hàm này — họ chỉ có cửa `isShiftOpen`.
 */
export function canMakeupWorkShift(
  status: string | null | undefined,
  proofDueAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (isShiftOpen(status)) return true;
  return isAfterShoot(status) && isMakeupProofOpen(proofDueAt, now);
}

/**
 * canMakeupUploadBeforeShoot — cửa SỚM của người trang điểm (quyết định 0261).
 *
 * Dòng "được xếp là nhận ca" (QĐ 0234, hôm nay là ảnh thẻ) không còn nút Nhận
 * ca đẩy đơn sang "Đang chụp". Nộp đủ cặp ảnh trước + sau là một trong ba mốc
 * đưa đơn đi tiếp, nên hai ô ảnh phải hiện TỪ NGÀY CHỤP khi đơn còn "Đã đặt" /
 * "Hợp đồng" — chờ "Đang chụp" mới hiện là vòng lẩn quẩn.
 *
 * Mốc ngày: cùng mốc với ô dán link ảnh gốc (QĐ 0196) và với máy chủ
 * (`proof.shootDayReached`). Dòng khác vẫn đi đường cũ: nhận ca → Đang chụp →
 * mới hiện ô ảnh.
 */
export function canMakeupUploadBeforeShoot(
  status: string | null | undefined,
  shootDate: string | null | undefined,
  acceptOnAssign: boolean,
  today: string = todayISO(),
): boolean {
  if (!acceptOnAssign) return false;
  if (status !== "booked" && status !== "contract") return false;
  const day = (shootDate ?? "").slice(0, 10);
  return day !== "" && day <= today;
}

/** Hôm nay theo đồng hồ máy người dùng — ai mở màn này cũng đang ở VN. */
function todayISO(): string {
  const now = new Date();
  const m = `${now.getMonth() + 1}`.padStart(2, "0");
  const d = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * isAfterShoot — đơn đã đi qua buổi chụp chưa. Gương của
 * `ekipschedule.isPostShootStatus` phía máy chủ.
 *
 * Cửa muộn chỉ mở về phía SAU buổi chụp: hạn nộp đếm từ 24 giờ sau ca, nên với
 * đơn còn "đã đặt" của tuần tới thì hạn ấy nằm ở tương lai — mở theo mỗi cái
 * hạn là bày ra hai ô ảnh cho một ca chưa hề diễn ra.
 */
function isAfterShoot(status: string | null | undefined): boolean {
  return status === "completed" || status === "delivered";
}
